import type {
  GameStateSnapshot,
  DeltaStateSnapshot,
  DeltaEntities,
  DestroyedEntities,
  WormSync,
  ProjectileSync,
  HomingMissileSync,
  MineSync,
  GraveSync,
  CrateSync,
  BarrelSync,
  FireSync,
  NapalmDropletSync,
  SheepSync,
  RaonSync,
} from './gameStateSync';

type EntitySync =
  | WormSync
  | ProjectileSync
  | HomingMissileSync
  | MineSync
  | GraveSync
  | CrateSync
  | BarrelSync
  | FireSync
  | NapalmDropletSync
  | SheepSync
  | RaonSync;

export function computeDelta(
  previous: GameStateSnapshot,
  current: GameStateSnapshot,
): DeltaStateSnapshot {
  const entities: Partial<DeltaEntities> = {};
  const destroyed: Partial<DestroyedEntities> = {};

  const entityTypes: Array<keyof DeltaEntities> = [
    'worms',
    'projectiles',
    'homingMissiles',
    'mines',
    'graves',
    'crates',
    'barrels',
    'fires',
    'napalmDroplets',
    'sheep',
    'raons',
  ];

  for (const type of entityTypes) {
    const prevEntities = (previous[type] ?? []) as EntitySync[];
    const currEntities = (current[type] ?? []) as EntitySync[];

    const prevMap = new Map(prevEntities.map((e) => [e.id, e]));
    const currMap = new Map(currEntities.map((e) => [e.id, e]));

    const changed: EntitySync[] = [];
    const destroyedIds: string[] = [];

    for (const curr of currEntities) {
      const prev = prevMap.get(curr.id);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(curr)) {
        changed.push(curr);
      }
    }

    for (const prev of prevEntities) {
      if (!currMap.has(prev.id)) {
        destroyedIds.push(prev.id);
      }
    }

    if (changed.length > 0) {
      (entities as any)[type] = changed;
    }

    if (destroyedIds.length > 0) {
      (destroyed as any)[type] = destroyedIds;
    }
  }

  return {
    seq: current.stateSeq,
    baseSeq: previous.stateSeq,
    tick: current.tick,
    turn: current.turn,
    entities,
    destroyed,
  };
}

export function applyDelta(
  base: GameStateSnapshot,
  delta: DeltaStateSnapshot,
): GameStateSnapshot | null {
  if (delta.baseSeq !== base.stateSeq) {
    return null;
  }

  const result: GameStateSnapshot = {
    ...base,
    tick: delta.tick,
    stateSeq: delta.seq,
    turn: delta.turn,
  };

  const entityTypes: Array<keyof DeltaEntities> = [
    'worms',
    'projectiles',
    'homingMissiles',
    'mines',
    'graves',
    'crates',
    'barrels',
    'fires',
    'napalmDroplets',
    'sheep',
    'raons',
  ];

  for (const type of entityTypes) {
    const baseEntities = [...((base[type] ?? []) as EntitySync[])];
    const changedEntities = delta.entities[type] as EntitySync[] | undefined;
    const destroyedIds = delta.destroyed[type] as string[] | undefined;

    if (changedEntities) {
      const changedMap = new Map(changedEntities.map((e) => [e.id, e]));
      for (let i = 0; i < baseEntities.length; i++) {
        const changed = changedMap.get(baseEntities[i].id);
        if (changed) {
          baseEntities[i] = changed;
          changedMap.delete(baseEntities[i].id);
        }
      }
      for (const newEntity of changedMap.values()) {
        baseEntities.push(newEntity);
      }
    }

    if (destroyedIds) {
      const destroyedSet = new Set(destroyedIds);
      (result as any)[type] = baseEntities.filter((e) => !destroyedSet.has(e.id));
    } else {
      (result as any)[type] = baseEntities;
    }
  }

  return result;
}
